import React, {
    createContext,
    useContext,
    useImperativeHandle,
    useState,
} from "react";

import PropTypes from "prop-types";

import { useBehaviors } from "./behaviors";
import { useWebmessenger } from "./webmessenger";

const SDKContext = createContext();

const SDKProvider = React.forwardRef(({ children }, ref) => {
    const {
        openWindow,
        closeWindow,
        notification,
        closeNotification,
        online,
        operator,
        maximized,
    } = useWebmessenger();
    const { triggerOpenWindowBehaviors } = useBehaviors();

    const [events, setEvents] = useState({});

    useImperativeHandle(
        ref,
        () => ({
            maximize: () => {
                if (online) {
                    if (notification) {
                        closeNotification();
                    }
                    openWindow(true);
                    triggerOpenWindowBehaviors();
                }
            },
            minimize: () => {
                closeWindow();
            },
            isMaximized: () => maximized,
            status: () => ({
                online,
                operator:
                    online && operator
                        ? {
                              name: operator.name,
                              title: operator.title,
                              picture: `${process.env.REACT_APP_FILE}/files/avatars/${operator.face}`,
                          }
                        : null,
            }),
            on: (eventType, listener) => {
                if (typeof events[eventType] !== "object") {
                    setEvents((prevEvents) => ({
                        ...prevEvents,
                        [eventType]: [],
                    }));
                }

                setEvents((prevEvents) => ({
                    ...prevEvents,
                    [eventType]: [...prevEvents[eventType], listener],
                }));
            },
            off: (eventType, listener) => {
                if (typeof events[eventType] === "object") {
                    const index = events[eventType].indexOf(listener);

                    if (index > -1) {
                        setEvents((prevEvents) => ({
                            ...prevEvents,
                            [eventType]: events[eventType].filter(
                                (eventListener) => eventListener !== listener
                            ),
                        }));
                    }
                }
            },
        }),
        [online, notification, events, operator, maximized]
    );

    const emit = (eventType, args) => {
        if (typeof events[eventType] === "object") {
            for (const listener of events[eventType]) {
                listener.call(this, args);
            }
        }
    };

    const value = React.useMemo(() => ({ emit }), [emit]);

    return <SDKContext.Provider value={value}>{children}</SDKContext.Provider>;
});

function useSDK() {
    const context = useContext(SDKContext);
    if (context === undefined) {
        throw new Error("useSDK must be used within a SDKProvider");
    }
    return context;
}

SDKProvider.propTypes = {
    children: PropTypes.element.isRequired,
};

export { SDKProvider, useSDK };
